import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "@/components/primitives";
import { PROJECTS } from "@/data/projects";

function ProjectCard({ p, i }) {
  const wide = i % 3 === 0;
  return (
    <Reveal delay={(i % 2) * 0.08} className={wide ? "lg:col-span-7" : "lg:col-span-5"}>
      <Link to={`/projects/${p.slug}`} className="group block" data-testid={`project-card-${p.slug}`}>
        <article>
          <div className="relative overflow-hidden">
            <img
              src={p.img}
              alt={`${p.name} · ${p.type}`}
              loading="lazy"
              className="aspect-[16/11] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
            />
            <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/15" />
            <span className="absolute right-5 top-5 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full bg-paper text-ink opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
              ↗
            </span>
            <span className="absolute bottom-5 left-5 bg-navy-deep/80 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.25em] text-paper backdrop-blur-sm">
              {p.type}
            </span>
          </div>
          <h3 className="mt-6 font-serif text-2xl text-ink transition-colors duration-300 group-hover:text-maroon lg:text-3xl">
            {p.name}
          </h3>
          {(() => {
            const meta = [["Type", p.type], ["Area", p.area], ["Location", p.location]].filter(([, v]) => v);
            return (
              meta.length > 0 && (
                <div
                  className={`mt-4 grid gap-4 border-t border-ink/15 pt-4 ${
                    meta.length === 1 ? "grid-cols-1" : meta.length === 2 ? "grid-cols-2" : "grid-cols-3"
                  }`}
                >
                  {meta.map(([k, v]) => (
                    <div key={k}>
                      <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink/45">{k}</p>
                      <p className="mt-1 text-sm text-ink/85">{v}</p>
                    </div>
                  ))}
                </div>
              )
            );
          })()}
          {p.scope && (
            <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.2em] text-navy">Scope · {p.scope}</p>
          )}
        </article>
      </Link>
    </Reveal>
  );
}

export default function Projects() {
  const highlight = PROJECTS.find((p) => p.highlight);

  return (
    <section id="work" className="bg-paper px-6 py-24 lg:px-12 lg:py-36" data-testid="projects-section">
      <div className="mx-auto max-w-[1440px]">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <Reveal>
              <Eyebrow index="04">Selected Work</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl text-ink sm:text-5xl">Our Projects</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 text-sm leading-relaxed text-ink/60">
                Select projects are combined works of <span className="font-medium text-ink">ARA Designs &amp; Turnkey Engineers</span>.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-ink/60">
              Offices, hotels, institutions and mission-critical spaces; delivered end to end, measured in square
              feet and in how people feel in them.
            </p>
          </Reveal>
        </div>

        {highlight && (
          <Reveal delay={0.1}>
            <Link
              to={`/projects/${highlight.slug}`}
              data-testid="highlight-project-link"
              className="group mt-14 grid gap-8 lg:grid-cols-12 lg:items-center lg:gap-12"
            >
              <div className="relative overflow-hidden lg:col-span-8" data-testid="highlight-project-media">
                <img
                  src={highlight.img}
                  alt={`${highlight.name} · ${highlight.type}`}
                  className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
                <span className="absolute left-6 top-6 bg-maroon px-3.5 py-2 text-[10px] font-medium uppercase tracking-[0.25em] text-paper">
                  Highlight Project
                </span>
              </div>
              <div className="lg:col-span-4">
                <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">{highlight.type}</p>
                <h3 className="mt-4 font-serif text-3xl text-ink transition-colors duration-300 group-hover:text-maroon lg:text-4xl">
                  {highlight.name}
                </h3>
                <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.18em] text-ink/50">
                  {[highlight.area, highlight.location].filter(Boolean).join(" · ")}
                </p>
                <span className="mt-8 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.2em] text-maroon">
                  View Project
                  <span className="inline-block h-px w-8 bg-maroon transition-all duration-300 group-hover:w-12" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="mt-20 grid grid-cols-1 gap-x-10 gap-y-16 lg:grid-cols-12" data-testid="projects-grid">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.slug} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
